import { FieldWrapper, getFieldErrorMessage } from "@birdwatch/ui/form";
import type { Extensions, Editor as TipTapEditor } from "@tiptap/core";
import { cn } from "cn";
import type { ReactNode, Ref } from "react";
import {
  BubbleMenu,
  Editor,
  type EditorContentHandle,
  InlineMenu,
  LinkDialog,
} from "@/components/ui/editor/rich-text";
import { useEditorContext } from "@/components/ui/editor/rich-text/rich-text-editor-provider";
import {
  type FieldBindingProps,
  PolyField,
  pickBinding,
} from "@/fields/_internal/poly-field";
import { RichTextContentField } from "@/fields/rich-text/rich-text-content-field";
import { RichTextSkeleton } from "@/fields/rich-text/rich-text-skeleton";

type RichTextFieldBaseProps = {
  label?: ReactNode;
  disabled?: boolean;
  /**
   * Content on its way (an AI draft): a skeleton stands in the editor box.
   * The editor stays mounted — hidden, not disabled — so `ref` keeps working
   * and no editable toggle reaches the form as an edit.
   */
  loading?: boolean;
  optional?: boolean;
  /** Rendered at the right end of the label row — see `FieldWrapper.labelAction`. */
  labelAction?: ReactNode;
  /** At the right end of the formatting toolbar, on its row (a character count, say). */
  toolbarEnd?: ReactNode;
  placeholder?: string;
  autoFocus?: boolean;
  emptyEditorClass?: string;
  /** Layout of the whole field block. Lands on the FieldWrapper root. */
  className?: string;
  /** Applied to the outer border wrapper. */
  inputClassName?: string;
  /** Applied to the inner `EditorContent`. */
  contentClassName?: string;
  /** Render the selection-anchored formatting BubbleMenu. Default `false`. */
  showBubbleMenu?: boolean;
  /** Render the floating block-level InlineMenu. Default `true`. */
  showInlineMenu?: boolean;
  /** Render the URL-insertion LinkDialog. Default `true`. */
  showLinkDialog?: boolean;
  /** Imperative editor handle: setContent / getContent / focus / blur / isEmpty / resetContent. */
  ref?: Ref<EditorContentHandle>;
  /** Extra Tiptap extensions (mentions, keyboard shortcuts). */
  extensions?: Extensions;
  /** Pasted / dropped images. */
  onUploadImages?: (files: File[]) => void;
  /** Fired AFTER the value is committed — for draft caches. */
  onValueChange?: (value: string) => void;
  onFocus?: () => void;
  /** Forwarded to EditorContent's onBlur — receives the Tiptap editor instance. */
  onBlur?: (editor: TipTapEditor) => void;
};

export type RichTextFieldProps = RichTextFieldBaseProps & FieldBindingProps;

type RichTextEditorBoxProps = {
  children: ReactNode;
  className?: string;
  hasError: boolean;
  disabled?: boolean;
};

function RichTextEditorBox({
  children,
  className,
  hasError,
  disabled,
}: RichTextEditorBoxProps) {
  const { editor } = useEditorContext("RichTextField");
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || !editor) return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest(".ProseMirror")) return;
    // Only skip focus for interactive elements inside the editor box —
    // closest() also matches ancestors like the surrounding Modal's
    // [role='dialog'], which would swallow every click.
    const interactive = target.closest(
      "button, [role='button'], [role='dialog'], a, input",
    );
    if (interactive && e.currentTarget.contains(interactive)) return;
    editor.commands.focus("end");
  };
  return (
    <div
      onClick={handleClick}
      className={cn(
        /* sorted */
        "overflow-hidden",
        "w-full",
        "outline outline-outline-dark-low rounded-lg cursor-text caret-text-dark-max",
        "transition-[color,box-shadow]",
        "aria-invalid:outline-outline-negative-max aria-invalid:focus-within:outline-outline-negative-max",
        disabled && "cursor-not-allowed",
        className,
      )}
      aria-invalid={hasError ? "true" : "false"}
    >
      {children}
    </div>
  );
}

export function RichTextField(props: RichTextFieldProps) {
  const {
    label,
    disabled,
    loading,
    optional,
    labelAction,
    toolbarEnd,
    placeholder,
    autoFocus,
    emptyEditorClass,
    className,
    inputClassName,
    contentClassName,
    showBubbleMenu = false,
    showInlineMenu = true,
    showLinkDialog = true,
    ref,
    extensions,
    onUploadImages,
    onValueChange,
    onFocus,
    onBlur,
  } = props;
  const binding = pickBinding(props);
  return (
    <PolyField<string> {...binding}>
      {(field) => {
        // `FieldOf` is the narrowed slice of the field api; `getFieldErrorMessage`
        // reads only `state.meta` (structural parameter).
        const error = getFieldErrorMessage(field);
        const hasError = !!error;

        return (
          <FieldWrapper
            label={label}
            error={error}
            disabled={disabled}
            optional={optional}
            labelAction={labelAction}
            className={className}
          >
            <Editor openInlineMenu={showInlineMenu}>
              <RichTextEditorBox
                hasError={hasError}
                disabled={disabled || loading}
                className={inputClassName}
              >
                {loading && (
                  // The editor's own padding and height, so the box keeps its size.
                  <div
                    className={cn("min-h-12", "px-4 py-3", contentClassName)}
                  >
                    <RichTextSkeleton />
                  </div>
                )}
                {showBubbleMenu && <BubbleMenu />}
                {toolbarEnd ? (
                  // The row takes the toolbar's own padding, so the buttons
                  // stay where they were and the end lines up with them.
                  <div className="flex items-center gap-2 p-2 pb-0">
                    {showInlineMenu && <InlineMenu className="p-0" />}
                    <div className="ml-auto mr-2 shrink-0">{toolbarEnd}</div>
                  </div>
                ) : (
                  showInlineMenu && <InlineMenu />
                )}
                {showLinkDialog && <LinkDialog />}
                <RichTextContentField
                  field={field}
                  ref={ref}
                  autoFocus={autoFocus}
                  placeholder={placeholder}
                  extensions={extensions}
                  onUploadImages={onUploadImages}
                  onValueChange={onValueChange}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  emptyEditorClass={emptyEditorClass}
                  className={cn(contentClassName, loading && "hidden")}
                  disabled={disabled}
                />
              </RichTextEditorBox>
            </Editor>
          </FieldWrapper>
        );
      }}
    </PolyField>
  );
}
